/**
 * closureEngine — lógica pura del cierre dinámico de auditoría.
 *
 * Sin dependencias de Vue ni IndexedDB. Recibe estructuras planas y devuelve
 * estructuras planas. Concentra la consolidación de conteos entre lotes, la
 * clasificación de movimientos posteriores al conteo y el cálculo de
 * diferencias proyectadas con la fórmula:
 *
 *   esperado    = countedQuantity − movsOut + movsIn
 *   diferencia  = stockSistemaActual − esperado
 *   acción      = |diff|<ε ? 'none' : (diff>0 ? 'out' : 'in')
 *
 * 'out' significa el sistema cree que hay MÁS que lo verificable → faltante
 * real → emitir stockOut. 'in' es lo opuesto.
 */

const EPSILON = 0.0001;

// Convención asumida (validable por el operador en preview).
// 'in'/'out' aplican Math.abs(quantity); 'signed' usa el signo nativo de
// quantity; 'neutral' se ignora. La validación humana en el desglose byCode
// permite detectar inversiones antes de emitir el ajuste contable.
export const EVENT_SIGNS = {
  SI: 'in',
  OC: 'in',
  GI: 'in',
  PE: 'in',
  DV: 'in',
  SO: 'out',
  OP: 'out',
  SAL: 'out',
  GS: 'out',
  PS: 'out',
  AJ: 'signed',
  TB: 'signed',
  CD: 'neutral',
};

const PREFIX_ALIASES = {
  'G-IN': 'GI',
  'G-OUT': 'GS',
};

/**
 * Extrae el prefijo de un order_number tipo "OP123456", "G-IN1234", "AJ001".
 */
export function classifyEvent(orderNumber) {
  if (!orderNumber) return { code: null, sign: 'neutral' };
  const match = String(orderNumber).match(/^([A-Z-]{2,5})\d+/i);
  if (!match) return { code: null, sign: 'neutral' };
  let code = match[1].toUpperCase();
  if (PREFIX_ALIASES[code]) code = PREFIX_ALIASES[code];
  const sign = EVENT_SIGNS[code] || 'neutral';
  return { code, sign };
}

function toDate(v) {
  if (v instanceof Date) return v;
  if (!v) return null;
  return new Date(v);
}

/**
 * Consolida los audits de varios lotes en un Map<ean, { ... }>.
 * Si un mismo SKU aparece en varios lotes:
 *  - countedQuantity se suma
 *  - lastUpdate se toma el máximo (último scan)
 *  - productCost / productPrice / nombre / refe se toman del audit más reciente
 *  - batchIds[] referencia todos los lotes que aportaron al SKU
 */
export function aggregateAuditsAcrossBatches(batches) {
  const byEan = new Map();
  const sourceBatchIds = [];

  for (const batch of batches || []) {
    if (!batch || !Array.isArray(batch.audits)) continue;
    sourceBatchIds.push(batch.id);

    for (const audit of batch.audits) {
      if (!audit) continue;
      const ean = audit.productId;
      if (!ean) continue;

      const counted = parseFloat(audit.countedQuantity) || 0;
      const lastUpdate = toDate(audit.lastUpdate);
      const existing = byEan.get(ean);

      if (!existing) {
        byEan.set(ean, {
          ean,
          countedQuantity: counted,
          lastUpdate,
          systemQuantity: parseFloat(audit.systemQuantity) || 0,
          productCost: parseFloat(audit.productCost) || 0,
          productPrice: parseFloat(audit.productPrice) || 0,
          productName: audit.productName,
          productRef: audit.productRef,
          batchIds: [audit.batchId],
        });
      } else {
        existing.countedQuantity += counted;
        if (lastUpdate && (!existing.lastUpdate || lastUpdate > existing.lastUpdate)) {
          existing.lastUpdate = lastUpdate;
          existing.productCost = parseFloat(audit.productCost) || existing.productCost;
          existing.productPrice = parseFloat(audit.productPrice) || existing.productPrice;
          existing.productName = audit.productName || existing.productName;
          existing.productRef = audit.productRef || existing.productRef;
        }
        if (!existing.batchIds.includes(audit.batchId)) {
          existing.batchIds.push(audit.batchId);
        }
      }
    }
  }

  return { byEan, sourceBatchIds };
}

/**
 * Indexa eventos por EAN para acceso O(1) por SKU contado.
 */
export function groupEventsByEan(events) {
  const map = new Map();
  for (const e of events || []) {
    const ean = e.ean || e.product_id;
    if (!ean) continue;
    if (!map.has(ean)) map.set(ean, []);
    map.get(ean).push(e);
  }
  return map;
}

/**
 * Calcula movimientos netos por EAN para los SKUs contados.
 * Filtra eventos por: order_date in (lastUpdate(SKU), closeAt].
 */
export function computeNetMovementsByEan({ aggregatedAudits, eventsByEan, closeAt }) {
  const result = new Map();
  const closeAtDate = toDate(closeAt);

  for (const [ean, audit] of aggregatedAudits) {
    const lastUpdate = audit.lastUpdate;
    const evs = (eventsByEan.get(ean) || []).filter((e) => {
      const od = toDate(e.order_date);
      if (!od) return false;
      if (lastUpdate && od < lastUpdate) return false;
      if (closeAtDate && od > closeAtDate) return false;
      return true;
    });

    let movsIn = 0;
    let movsOut = 0;
    const byCode = {};
    const samples = [];

    for (const e of evs) {
      const { code, sign } = classifyEvent(e.order_number);
      const q = parseFloat(e.quantity) || 0;

      if (sign === 'in') {
        movsIn += Math.abs(q);
      } else if (sign === 'out') {
        movsOut += Math.abs(q);
      } else if (sign === 'signed') {
        if (q > 0) movsIn += q;
        else movsOut += Math.abs(q);
      }
      // 'neutral' → ignorado para el neto

      if (code) {
        byCode[code] = (byCode[code] || 0) + q;
      }
      if (samples.length < 10) {
        samples.push({
          order_number: e.order_number,
          order_date: e.order_date,
          quantity: q,
          code,
          sign,
        });
      }
    }

    result.set(ean, { movsIn, movsOut, byCode, samples, eventCount: evs.length });
  }

  return result;
}

/**
 * Construye la preview del cierre. Cada fila representa un SKU contado y
 * declara la acción a tomar. Los SKUs no contados nunca aparecen
 * (operación cíclica — no se ajustan).
 */
export function buildClosurePreview({
  aggregatedAudits,
  currentInventoryByEan,
  eventsByEan,
  closeAt,
}) {
  const movsByEan = computeNetMovementsByEan({
    aggregatedAudits,
    eventsByEan,
    closeAt,
  });

  const rows = [];
  const warnings = [];
  let skusCuadrados = 0;
  let skusFaltante = 0;
  let skusSobrante = 0;
  let totalFaltanteUnidades = 0;
  let totalSobranteUnidades = 0;
  let valorFaltante = 0;
  let valorSobrante = 0;

  for (const [ean, audit] of aggregatedAudits) {
    const sys = currentInventoryByEan.get(ean);
    const movs = movsByEan.get(ean) || { movsIn: 0, movsOut: 0, byCode: {}, samples: [], eventCount: 0 };

    const baseRow = {
      ean,
      refe: audit.productRef,
      nombre: audit.productName,
      countedQuantity: audit.countedQuantity,
      countedAt: audit.lastUpdate,
      systemQuantityInitial: audit.systemQuantity,
      productCost: audit.productCost,
      productPrice: audit.productPrice,
      movsIn: movs.movsIn,
      movsOut: movs.movsOut,
      movsByCode: movs.byCode,
      eventCount: movs.eventCount,
      samples: movs.samples,
      batchIds: audit.batchIds,
    };

    if (!sys) {
      const w = {
        ean,
        message: `Producto ${ean} (${audit.productName}) no existe en catálogo actual`,
      };
      warnings.push(w);
      rows.push({
        ...baseRow,
        systemQuantityNow: null,
        marca: null,
        categoria: null,
        color: null,
        talla: null,
        expected: null,
        difference: null,
        valuation: 0,
        action: 'none',
        warning: w.message,
      });
      continue;
    }

    const sysQty = parseFloat(sys.cantidad) || 0;
    const expected = audit.countedQuantity - movs.movsOut + movs.movsIn;
    const diff = sysQty - expected;
    const absDiff = Math.abs(diff);
    const valuation = absDiff * (audit.productCost || 0);

    let action = 'none';
    if (absDiff >= EPSILON) action = diff > 0 ? 'out' : 'in';

    if (action === 'none') {
      skusCuadrados++;
    } else if (action === 'out') {
      skusFaltante++;
      totalFaltanteUnidades += absDiff;
      valorFaltante += valuation;
    } else {
      skusSobrante++;
      totalSobranteUnidades += absDiff;
      valorSobrante += valuation;
    }

    rows.push({
      ...baseRow,
      marca: sys.marca,
      categoria: sys.categoria,
      color: sys.color || sys.colorName,
      talla: sys.talla,
      systemQuantityNow: sysQty,
      expected,
      difference: diff,
      valuation,
      action,
    });
  }

  const summary = {
    skusContados: rows.length,
    skusCuadrados,
    skusFaltante,
    skusSobrante,
    totalFaltanteUnidades,
    totalSobranteUnidades,
    valorFaltante,
    valorSobrante,
  };

  return { rows, summary, warnings };
}

/**
 * Construye los dos payloads put_stock (stockOut + stockIn) a partir de las
 * filas de preview. Cualquier fila con action==='none' se ignora.
 *
 * El shape replica IAOut.vue:1190-1210 / IAIn.vue:1401-1420 — items con
 * `cantidad` (no `quantity`), payments[0] con código del concepto.
 */
export function buildPutStockPayloads(rows, ctx) {
  const {
    store,
    userId,
    seller,
    authorizer,
    closureId,
    paymentCodeOut = 'AUDIT_CLOSE_OUT',
    paymentCodeIn = 'AUDIT_CLOSE_IN',
  } = ctx || {};

  const outRows = rows.filter((r) => r.action === 'out');
  const inRows = rows.filter((r) => r.action === 'in');

  const buildItems = (subset) =>
    subset.map((r) => ({
      ean: r.ean,
      refe: r.refe || '',
      nombre: r.nombre || '',
      marca: r.marca || '',
      categoria: r.categoria || '',
      color: r.color || '',
      talla: r.talla || '',
      cantidad: Math.abs(r.difference),
      precio: r.productPrice || 0,
      costo: r.productCost || 0,
    }));

  const sumValuation = (subset) =>
    subset.reduce((s, r) => s + Math.abs(r.difference) * (r.productCost || 0), 0);

  const paidTo = authorizer
    ? {
        PartyIdentification:
          (authorizer.store_id || store) + '-' + (authorizer.user_email || ''),
        PartyName: authorizer.user_name || authorizer.user_email || '',
        client_email: authorizer.user_email || '',
        client_phone: '',
        typeid: '13',
      }
    : null;

  const stockOutPayload = outRows.length
    ? {
        doc: {
          doc_type: 'stockOut',
          doc_value: sumValuation(outRows),
          store,
          userId,
        },
        items: buildItems(outRows),
        paidTo,
        seller,
        store,
        payments: [
          {
            payment_code: paymentCodeOut,
            payment_concept: 'Cierre auditoría ' + closureId + ' - faltantes',
            payment_reference: closureId,
            payment_value: sumValuation(outRows),
          },
        ],
      }
    : null;

  const stockInPayload = inRows.length
    ? {
        doc: {
          doc_type: 'stockIn',
          doc_value: sumValuation(inRows),
          store,
          userId,
        },
        items: buildItems(inRows),
        paidTo,
        seller,
        store,
        payments: [
          {
            payment_code: paymentCodeIn,
            payment_concept: 'Cierre auditoría ' + closureId + ' - sobrantes',
            payment_reference: closureId,
            payment_value: sumValuation(inRows),
          },
        ],
      }
    : null;

  return { stockOutPayload, stockInPayload };
}

/**
 * Convierte un array de productos del catálogo en Map<ean, item> para lookup.
 */
export function indexInventoryByEan(inventory) {
  const map = new Map();
  for (const item of inventory || []) {
    if (item && item.ean) map.set(item.ean, item);
  }
  return map;
}
